import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Package, Store, ArrowRight, CheckCircle2 } from "lucide-react";

export default function RegisterStoreOwner() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    owner_name: "",
    store_name: "",
    email: "",
    phone: "",
    business_address: "",
    password: "",
    confirm: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (form.password !== form.confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/register-store-owner", {
        owner_name: form.owner_name,
        store_name: form.store_name,
        email: form.email.trim().toLowerCase(),
        phone: form.phone,
        business_address: form.business_address,
        password: form.password,
      });
      toast.success(data?.message || "Registration submitted.");
      setDone(true);
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-white grid place-items-center px-6">
        <div data-testid="register-success" className="max-w-md w-full text-center border border-slate-200 dark:border-slate-800 rounded-lg p-10 bg-slate-100/40 dark:bg-slate-900/40">
          <div className="w-14 h-14 rounded-full bg-green-500/10 border border-green-500/40 grid place-items-center mx-auto mb-5">
            <CheckCircle2 className="w-7 h-7 text-green-400" />
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Submitted</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-3">
            Your registration has been submitted and is waiting for admin approval.
          </p>
          <div className="mt-8 flex flex-col gap-2">
            <Button
              data-testid="register-go-login-btn"
              onClick={() => navigate("/login")}
              className="bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-medium h-11"
            >
              Go to Login <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Link to="/" className="text-xs text-slate-500 hover:text-slate-700 hover:dark:text-slate-300 mt-2">
              ← Back to site
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-white py-12 px-6">
      <div className="max-w-2xl mx-auto">
        <Link to="/" data-testid="register-back-link" className="inline-flex items-center gap-2 text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-500 hover:text-slate-900 hover:dark:text-white mb-8">
          ← Back to site
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-md bg-cyan-400 text-slate-950 grid place-items-center">
            <Store className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-cyan-400">
            / Store Owner Registration
          </div>
        </div>
        <h1 className="font-display text-4xl font-bold tracking-tight mb-2">Open a store on DeliveryBoss</h1>
        <p className="text-slate-600 dark:text-slate-400 mb-8 max-w-xl">
          Tell us about your business. Once an admin approves your account, you can log in and start creating packages.
        </p>

        <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-5 border border-slate-200 dark:border-slate-800 rounded-lg p-6 bg-slate-100/40 dark:bg-slate-900/40">
          <Field label="Owner Full Name">
            <Input
              data-testid="reg-owner-name"
              required
              value={form.owner_name}
              onChange={handleChange("owner_name")}
              className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800"
            />
          </Field>
          <Field label="Store Name">
            <Input
              data-testid="reg-store-name"
              required
              value={form.store_name}
              onChange={handleChange("store_name")}
              className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800"
            />
          </Field>
          <Field label="Email">
            <Input
              data-testid="reg-email"
              required
              type="email"
              value={form.email}
              onChange={handleChange("email")}
              className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800"
            />
          </Field>
          <Field label="Phone Number">
            <Input
              data-testid="reg-phone"
              required
              value={form.phone}
              onChange={handleChange("phone")}
              className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800"
            />
          </Field>
          <div className="md:col-span-2">
            <Field label="Business Address">
              <Textarea
                data-testid="reg-address"
                required
                value={form.business_address}
                onChange={handleChange("business_address")}
                className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white"
                rows={2}
              />
            </Field>
          </div>
          <Field label="Password">
            <Input
              data-testid="reg-password"
              required
              type="password"
              value={form.password}
              onChange={handleChange("password")}
              className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800"
            />
          </Field>
          <Field label="Confirm Password">
            <Input
              data-testid="reg-confirm"
              required
              type="password"
              value={form.confirm}
              onChange={handleChange("confirm")}
              className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800"
            />
          </Field>

          {error && (
            <div data-testid="reg-error" className="md:col-span-2 text-sm text-red-400 bg-red-500/10 border border-red-500/30 px-3 py-2 rounded-md">
              {error}
            </div>
          )}

          <div className="md:col-span-2 flex flex-wrap gap-3 items-center justify-end pt-2">
            <Link to="/login" className="text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 hover:dark:text-white">
              Already approved? Sign in
            </Link>
            <Button
              type="submit"
              data-testid="reg-submit-btn"
              disabled={submitting}
              className="bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-medium h-11 px-6"
            >
              {submitting ? "Submitting…" : "Submit Registration"}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-600 dark:text-slate-400">{label}</Label>
      {children}
    </div>
  );
}
